import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';
import { isAxiosError } from 'axios';
import { Error, LoaderPage, MaintenanceView } from '@utilities';

import { MAINTENANCE_MESSAGE, readInstantSurveyTypes, SURVEY_TYPES } from '@api/instant_survey';
import type { InstantSurveyType } from '@api/instant_survey/types';
import { GATES } from '@constants';
import ModuleList from '../../components/ModuleList';
import SurveyNetworkError from '../../components/SurveyNetworkError';
import { default as NumeratorLogo } from '../../components/Header/assets/data-wave-logo.svg?react';
import Module from '../../layouts/Module';
import ResearchHub from './components/ResearchHub';
import { mapSurveyTypeToRoute, SURVEY_TABS } from './utilities/helpers';

const SURVEY_TYPE_GATES: Partial<Record<string, string>> = {
  [SURVEY_TYPES.SEQUENTIAL_MONADIC]: GATES.SEQUENTIAL_MONADIC,
  [SURVEY_TYPES.VAN_WESTENDORP]: GATES.VAN_WESTENDORP,
  [SURVEY_TYPES.VIDEO]: GATES.QUESTION_TYPE_VIDEO,
};

const getRequestError = (error: unknown) => {
  if (!isAxiosError(error)) return {};

  return { code: error.code, status: error.response?.status };
};

const Surveys = () => {
  const [error, setError] = useState<unknown>();
  const [isLoading, setIsLoading] = useState(true);
  const [surveyTypes, setSurveyTypes] = useState<InstantSurveyType[]>([]);
  const { client } = useStatsigClient();
  const isInstantSurveyMaintenance = client.checkGate(GATES.INSTANT_SURVEY_MAINTENANCE);
  const isResearchHubEnabled = client.checkGate(GATES.SURVEY_MENU_REFACTOR);
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isInstantSurveyMaintenance) return;

    const getSurveyTypes = async () => {
      const controller = new AbortController();
      requestRef.current = controller;

      try {
        const { results } = await readInstantSurveyTypes({ signal: controller.signal });
        setSurveyTypes(
          results.filter((surveyType: InstantSurveyType) => {
            const gate = SURVEY_TYPE_GATES[surveyType.id];
            return gate ? client.checkGate(gate) : true;
          })
        );
      } catch (requestError) {
        if (!controller.signal.aborted) setError(requestError);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    getSurveyTypes();
    return () => requestRef.current?.abort();
  }, [isInstantSurveyMaintenance]);

  if (isInstantSurveyMaintenance) {
    return <MaintenanceView heading={MAINTENANCE_MESSAGE} logo={<NumeratorLogo />} />;
  }
  if (isLoading) return <LoaderPage />;
  if (error) {
    const { code, status } = getRequestError(error);
    return code === 'ERR_NETWORK' ? <SurveyNetworkError /> : <Error status={status} />;
  }

  if (isResearchHubEnabled) return <ResearchHub surveyTypes={surveyTypes} />;

  const surveyRoutes = [
    {
      ...SURVEY_TABS[0],
      routes: surveyTypes
        .filter(({ label }) => label === SURVEY_TABS[0].id)
        .map(mapSurveyTypeToRoute),
    },
    ...(() => {
      const surveyLabs = surveyTypes
        .filter(({ label }) => label === SURVEY_TABS[1].id)
        .map(mapSurveyTypeToRoute);
      return surveyLabs.length > 0 ? [{ ...SURVEY_TABS[1], routes: surveyLabs }] : [];
    })(),
  ];

  return (
    <Module route={{ routes: surveyRoutes }}>
      <Routes>
        {surveyRoutes.map((route) => (
          <Route
            element={<ModuleList routes={route.routes} show_favorites={false} type="survey" />}
            key={route.id}
            path={route.path}
          />
        ))}
        <Route element={<Navigate replace to={surveyRoutes[0].path} />} path="*" />
      </Routes>
    </Module>
  );
};

export default Surveys;
